import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';

import { Input, InputTrailingIcon } from '@/ux-common/components/input/input';
import { useGroupField, useInputGroup } from '@/ux-common/components/input/input.context';
import {
  isEmptyTriggerValue,
  splitTriggerChildren,
} from '@/ux-common/components/input/split-trigger-children';
import { cn } from '@/ux-common/lib/utils';

/**
 * Select — a form field that picks one value from a list, on @radix-ui/react-select
 * (the WAI-ARIA select-only combobox: `role="combobox"` on the trigger, a real
 * `listbox` of `option`s, typeahead, and a native form value through `name`).
 *
 * It looks like the Input it sits among and opens a list drawn as a DropdownMenu
 * panel. It is an `Input` group, so `InputLabel` and `InputHelper` wire themselves
 * to the trigger with no ids at the call site, and every root state class
 * (`.input-error`, `.input-warning`, `.input-lg`, …) applies.
 *
 * Use this — not an `InputField readOnly` opening a DropdownMenu — whenever the
 * user is choosing a value: a textbox cannot be a popup trigger, and a menu has no
 * value. For choosing several values, use DropdownInput: the same field, opening a
 * DropdownMenu of checkbox rows.
 *
 * @example
 * ```tsx
 * <Select value={mode} onValueChange={setMode} name="mode">
 *   <InputLabel>Strategy</InputLabel>
 *   <SelectTrigger placeholder="Choose a strategy" />
 *   <SelectContent>
 *     <SelectItem value="auto">Automatic</SelectItem>
 *     <SelectItem value="manual">Manual</SelectItem>
 *   </SelectContent>
 *   <InputHelper>Applies on the next cycle.</InputHelper>
 * </Select>
 * ```
 */

export interface SelectProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> {
  /** Class(es) for the `.input` group that wraps the field. */
  className?: string;
  /**
   * The group's validation state, as on `Input` — applies `.input-{state}` and
   * reaches the group's slots.
   */
  state?: React.ComponentProps<typeof Input>['state'];
  /**
   * Refuses the field and says why — a view-only session, a missing permission.
   * Unlike `disabled`, the trigger stays focusable and hoverable, so a keyboard user
   * reaches it and a `withTooltip` on it still fires (a natively disabled button gets
   * no pointer or focus events). It reads and looks disabled (`aria-disabled`), never
   * opens, ignores typeahead, and announces the reason as its description without
   * depending on hover. Wins over `disabled` when both are set.
   */
  disabledReason?: React.ReactNode;
  /**
   * The group's `data-component` marker — `"Select"` unless you name it. The Radix
   * root renders no element, so this is the field's outermost element: a call site
   * annotating its own component root sets it here.
   */
  'data-component'?: string;
}

/**
 * Select — the Radix root inside an `.input.select` group. Root props (`value`,
 * `defaultValue`, `onValueChange`, `name`, `required`, `disabled`, `open`, …) pass
 * through; `className` and `state` go to the group.
 *
 * A refused field (`disabledReason`) is held shut here, whatever opens it: the
 * root is controlled closed and ignores open requests. The reason is rendered once,
 * visually hidden, for the trigger's `aria-describedby`.
 *
 * `onValueChange` never receives `''`. Inside a `<form>`, Radix renders a hidden
 * native `<select>` whose `change` it forwards to `onValueChange`, and it fires that
 * `change` itself whenever `value` changes. The native options exist only once the
 * closed list has mounted, so a `value` set in the mount commit — react-hook-form's
 * `reset` in a mount effect — finds no matching option, reads `''`, and reports it:
 * the field blanks a saved value with no user action, and the form counts it as
 * changed. `''` is never a real choice — Radix before 2.3 throws on a `SelectItem`
 * with `value=""`, so the registry does not support one; use a sentinel such as
 * `"all"` — so dropping it loses nothing. Clearing still works through `value`.
 */
export function Select({
  className,
  state,
  children,
  disabledReason,
  onValueChange,
  'data-component': dataComponent = 'Select',
  ...props
}: SelectProps) {
  const reasonId = React.useId();
  const refused = disabledReason != null && disabledReason !== false && disabledReason !== '';
  const handleValueChange = onValueChange
    ? (value: string) => {
        if (value !== '') onValueChange(value);
      }
    : undefined;
  const rootProps = {
    ...props,
    onValueChange: handleValueChange,
    ...(refused && { disabled: false, open: false, onOpenChange: undefined }),
  };

  return (
    <SelectPrimitive.Root {...rootProps}>
      <SelectRootContext.Provider
        value={{ disabled: Boolean(rootProps.disabled), reasonId: refused ? reasonId : undefined }}
      >
        <Input data-component={dataComponent} state={state} className={cn('select', className)}>
          {children}
          {refused && (
            <span id={reasonId} className="sr-only">
              {disabledReason}
            </span>
          )}
        </Input>
      </SelectRootContext.Provider>
    </SelectPrimitive.Root>
  );
}

/**
 * The root's state, for the trigger. `disabled`: Radix disables the trigger button
 * itself, but the trigger registers its OWN `disabled` with the Input group — so
 * without this, `<Select disabled>` left the group believing the field was enabled.
 * `reasonId`: set while the root is refused (`disabledReason`), naming the reason.
 */
const SelectRootContext = React.createContext<{ disabled: boolean; reasonId?: string }>({
  disabled: false,
});

Select.displayName = 'Select';

export type SelectTriggerProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  /** Shown in the field until a value is chosen. */
  placeholder?: React.ReactNode;
};

/**
 * SelectTrigger — the field. A `<button role="combobox">` drawn as the bordered
 * `.input-field.input-trigger` (the look it shares with DropdownInput), showing the chosen option's text (or `placeholder`) and a
 * trailing chevron. It joins the enclosing group, so `InputLabel`'s `htmlFor` and
 * every `InputHelper`'s description reach it.
 *
 * For a leading icon, pass an `InputLeadingIcon` as a child: it renders before the
 * value, sized, coloured and spaced as on InputField, and decorative.
 *
 * For `state="new"`, pass an `InputValueChange` as a child: it renders after the
 * value, before the chevron, the value beside it hugs its text, and its "Previous
 * value …" leads the trigger's accessible description.
 *
 * For an error or warning glyph, pass an `InputStateIcon`: it joins the trailing
 * cluster before the chevron. Not `InputStateInfo` — it is a button, and this is
 * already one; passed anyway, it is dropped with a dev warning.
 */
export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(
  (
    {
      className,
      placeholder,
      children,
      id,
      disabled,
      'aria-describedby': ariaDescribedBy,
      onClick,
      onKeyDown,
      onPointerDown,
      ...props
    },
    ref
  ) => {
    const { disabled: rootDisabled, reasonId } = React.useContext(SelectRootContext);
    const refused = reasonId !== undefined;

    // A click on the `InputLabel` reaches this button as a plain click with no
    // press on the button itself. Radix opens the list on such a click while it
    // believes the last pointer was a touch — which it assumes until the first
    // mouse press — so the label opened the list at first and only focused it
    // later. A label click now only focuses, like a native <select> and like
    // DropdownInput. Mouse, touch and keyboard opening are untouched: a real
    // press records itself here first. Calling `preventDefault` skips Radix's own
    // click handler (composeEventHandlers checks it), so focus is done here.
    const pressedRef = React.useRef(false);
    const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
      pressedRef.current = true;
      onPointerDown?.(event);
    };
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!pressedRef.current && !event.defaultPrevented) {
        event.currentTarget.focus();
        event.preventDefault();
      }
      pressedRef.current = false;
    };

    // A refused trigger is not natively disabled — it must keep focus and hover — so
    // it blocks Radix here instead. Each handler is composed ahead of Radix's, which
    // skips its own when the event is default-prevented: no open from a press, a
    // click or Enter / Space / arrows, and no typeahead (on a closed Select it
    // changes the value). Tab passes, so focus moves on. A prevented pointerdown
    // also skips the browser's focus, so focus is given by hand.
    const refusedHandlers = refused
      ? {
          onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => {
            event.currentTarget.focus();
            event.preventDefault();
          },
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.currentTarget.focus();
            event.preventDefault();
          },
          onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => {
            if (event.key !== 'Tab') event.preventDefault();
          },
        }
      : { onPointerDown: handlePointerDown, onClick: handleClick, onKeyDown };

    // Joins the group the way every field type does (see useGroupField). Disabled
    // on the root or the trigger — the same union Radix applies to the button — or
    // refused, which the group treats as disabled too.
    const { controlId, setControlRef, describedBy } = useGroupField<HTMLButtonElement>({
      id,
      ref,
      disabled: rootDisabled || Boolean(disabled) || refused,
      readOnly: false,
      ariaDescribedBy,
    });

    // An `InputLeadingIcon` and an `InputValueChange` (`state="new"`) render beside
    // the value, not in it. The rest of `children` is the value, and the chosen
    // option's text when empty — so an icon alone still shows the chosen option.
    const {
      leadingIcon,
      value,
      valueChange: rawValueChange,
      stateIcon,
    } = splitTriggerChildren(children);

    // The previous value joins the DESCRIPTION, first, ahead of the group's
    // helpers: "Display mode" (name), "Bar" (the combobox's value), "Previous value
    // Line" (description). A combobox is named by its label and exposes its current
    // value separately, so the name is no place for the old value; DropdownInput, a
    // plain menu button with no value of its own, carries both in its name instead.
    const valueChangeId = rawValueChange?.props.id ?? `${controlId}-value-change`;
    const valueChange = rawValueChange && React.cloneElement(rawValueChange, { id: valueChangeId });
    // A refusal's reason leads, then the previous value, then the helpers.
    const triggerDescribedBy =
      [reasonId, valueChange ? valueChangeId : undefined, describedBy].filter(Boolean).join(' ') ||
      undefined;

    return (
      <SelectPrimitive.Trigger
        ref={setControlRef}
        id={controlId}
        disabled={refused ? false : disabled}
        aria-describedby={triggerDescribedBy}
        data-component="SelectTrigger"
        className={cn('input-field input-trigger select-trigger', className)}
        {...props}
        {...refusedHandlers}
        {...(refused && { 'aria-disabled': true, 'data-disabled': '' })}
      >
        {leadingIcon}
        <span className="input-control select-value">
          {isEmptyTriggerValue(value) ? <SelectPrimitive.Value placeholder={placeholder} /> : value}
        </span>
        {valueChange}
        {stateIcon}
        <InputTrailingIcon className="input-chevron select-chevron">
          <ChevronDown />
        </InputTrailingIcon>
      </SelectPrimitive.Trigger>
    );
  }
);

SelectTrigger.displayName = 'SelectTrigger';

export type SelectContentProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>;

/**
 * SelectContent — the list. Portalled, opening 2px below the field, at least 250px
 * (the menu's floor) and at least as wide as the field. It IS a DropdownMenu panel (`.dropdown-menu`): Radix Select sets the
 * same data-state / data-highlighted / data-disabled attributes as Radix
 * DropdownMenu, so the menu's row rules style the options with no second copy.
 *
 * Named after the group's `InputLabel` ("Strategy"), falling back to the trigger.
 * Radix renders the `listbox` with no name of its own, which leaves screen readers
 * announcing an unnamed list. A consumer's own `aria-labelledby` wins.
 */
export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(
  (
    {
      className,
      children,
      position = 'popper',
      sideOffset = 2,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    const group = useInputGroup();
    const labelledBy = ariaLabelledBy ?? group?.labelId ?? group?.controlId;

    return (
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          ref={ref}
          aria-labelledby={labelledBy}
          data-component="SelectContent"
          position={position}
          sideOffset={sideOffset}
          className={cn('dropdown-menu select-content', className)}
          {...props}
        >
          <SelectPrimitive.Viewport className="select-viewport">
            {children}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  }
);

SelectContent.displayName = 'SelectContent';

export type SelectItemProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>;

/**
 * SelectItem — one option, drawn as a DropdownMenu radio row (the registry `.radio`
 * in its inverse variant, filled when the option is the chosen one). `children` is
 * the option's text, which is also what the field shows once it is chosen.
 *
 * The markup is DropdownMenuRadioItem's, copied: keep the two in step (a test
 * compares them).
 */
export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-component="SelectItem"
    className={cn('dropdown-menu-item dropdown-menu-radio-item select-item', className)}
    {...props}
  >
    <span aria-hidden="true" className="radio radio-inverse">
      <SelectPrimitive.ItemIndicator className="radio-indicator" />
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));

SelectItem.displayName = 'SelectItem';

export type SelectGroupProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group>;

/**
 * SelectGroup — a group of options under a `SelectLabel`. Drawn as a DropdownMenu
 * group (`.dropdown-menu-group`): rows 2px apart, the label flush on the first.
 */
export const SelectGroup = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Group>,
  SelectGroupProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Group
    ref={ref}
    data-component="SelectGroup"
    className={cn('dropdown-menu-group select-group', className)}
    {...props}
  />
));

SelectGroup.displayName = 'SelectGroup';

export type SelectLabelProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>;

/** SelectLabel — a non-interactive group heading in the list, set in caps. */
export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  SelectLabelProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    data-component="SelectLabel"
    className={cn('dropdown-menu-label select-label', className)}
    {...props}
  />
));

SelectLabel.displayName = 'SelectLabel';

export type SelectSeparatorProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>;

/** SelectSeparator — a 1px rule between groups. */
export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  SelectSeparatorProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    data-component="SelectSeparator"
    className={cn('dropdown-menu-separator select-separator', className)}
    {...props}
  />
));

SelectSeparator.displayName = 'SelectSeparator';
